import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@/components/ui/AppIcon';
import { useScrollReveal } from '@/hooks/useReveal';
import { fetchGalleryImages, mediaUrl } from '@/lib/api';

const DEFAULT_HERO = '/assets/images/swaranjali-melb.jpg';

function mapGalleryImage(image, index) {
    return {
        id: `api-${image.id}`,
        title: image.title || `Gallery Photo ${index + 1}`,
        location: 'Australia',
        year: new Date(image.createdAt || Date.now()).getFullYear().toString(),
        category: 'Gallery',
        images: [{ src: mediaUrl(image.imageURL), alt: image.title || 'Gallery image' }],
    };
}

function EventGalleryLightbox({ events, state, onClose, onPrev, onNext }) {
    const event = events[state.eventIndex];
    const image = event?.images[state.imageIndex];

    useEffect(() => {
        const onKey = (eventKey) => {
            if (eventKey.key === 'Escape') onClose();
            if (eventKey.key === 'ArrowLeft') onPrev();
            if (eventKey.key === 'ArrowRight') onNext();
        };
        if (state.open) {
            document.addEventListener('keydown', onKey);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [state.open, onClose, onPrev, onNext]);

    if (!state.open || !event || !image) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" role="dialog" aria-modal="true" aria-label={`Viewing ${event.title}`}>
            <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={onClose} />
            <div className="relative z-10 flex flex-col items-center w-full max-w-5xl mx-4">
                <button
                    type="button"
                    onClick={onClose}
                    className="fixed top-5 right-5 z-20 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors p-0 leading-none sm:top-6 sm:right-6"
                    aria-label="Close lightbox"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="block h-[18px] w-[18px] shrink-0" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <div className="relative w-full rounded-2xl overflow-hidden border border-white/10 shadow-2xl" style={{ maxHeight: '75vh' }}>
                    <img src={image.src} alt={image.alt} className="w-full h-full object-contain" style={{ maxHeight: '75vh' }} />
                </div>
                <div className="mt-4 text-center">
                    <p className="text-white font-display font-semibold text-lg">{event.title}</p>
                    <p className="text-white/60 text-sm mt-1">{event.location} · {event.year} · {event.category}</p>
                </div>
                <div className="flex items-center gap-4 mt-5">
                    <button type="button" onClick={onPrev} className="w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-primary/80 hover:border-primary transition-all duration-200" aria-label="Previous image">
                        <Icon name="ChevronLeftIcon" size={18} />
                    </button>
                    <span className="text-white/50 text-xs">{state.eventIndex + 1} / {events.length}</span>
                    <button type="button" onClick={onNext} className="w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-primary/80 hover:border-primary transition-all duration-200" aria-label="Next image">
                        <Icon name="ChevronRightIcon" size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function EventGalleryPageContent() {
    const [activeCategory, setActiveCategory] = useState('All');
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [heroImage, setHeroImage] = useState(DEFAULT_HERO);
    const [lightbox, setLightbox] = useState({ open: false, eventIndex: 0, imageIndex: 0 });

    useEffect(() => {
        let cancelled = false;

        fetchGalleryImages()
            .then((images) => {
                if (cancelled) return;
                const apiEvents = (images ?? []).map(mapGalleryImage);
                setEvents(apiEvents);
                if (apiEvents.length) {
                    setHeroImage(apiEvents[0].images[0].src);
                } else {
                    setHeroImage(DEFAULT_HERO);
                }
            })
            .catch(() => {
                if (!cancelled) setEvents([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useScrollReveal([activeCategory, events.length, loading]);

    const categories = useMemo(() => {
        const unique = [...new Set(events.map((event) => event.category))];
        return ['All', ...unique];
    }, [events]);

    const filteredEvents = activeCategory === 'All'
        ? events
        : events.filter((event) => event.category === activeCategory);

    const openLightbox = useCallback((eventIndex) => {
        setLightbox({ open: true, eventIndex, imageIndex: 0 });
    }, []);

    const closeLightbox = useCallback(() => {
        setLightbox((prev) => ({ ...prev, open: false }));
    }, []);

    const prevImage = useCallback(() => {
        setLightbox((prev) => ({
            ...prev,
            eventIndex: (prev.eventIndex - 1 + filteredEvents.length) % filteredEvents.length,
            imageIndex: 0,
        }));
    }, [filteredEvents.length]);

    const nextImage = useCallback(() => {
        setLightbox((prev) => ({
            ...prev,
            eventIndex: (prev.eventIndex + 1) % filteredEvents.length,
            imageIndex: 0,
        }));
    }, [filteredEvents.length]);

    return (
        <>
            <section className="relative min-h-[45vh] flex items-end overflow-hidden" aria-label="Event Gallery hero">
                <div className="absolute inset-0">
                    <img src={heroImage} alt="Awakasha Entertainment live event" className="w-full h-full object-cover object-center" style={{ objectPosition: 'center 30%' }} />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-black/40" />
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-12 pt-32">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                        <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                        <Icon name="ChevronRightIcon" size={12} />
                        <span className="text-primary">Event Gallery</span>
                    </div>
                    <p className="section-label mb-3">Event Gallery</p>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground leading-tight max-w-2xl">
                        Moments from Our <span className="gold-gradient">Events</span>
                    </h1>
                    <p className="text-foreground/70 text-base mt-4 max-w-xl leading-relaxed">
                        Relive the magic — highlights from concerts, theatre, film screenings, and cultural experiences across Australia.
                    </p>
                </div>
            </section>

            <section className="bg-secondary border-y border-border/40 py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-3 gap-4 sm:gap-8">
                        {[
                            { value: '50+', label: 'Events Hosted' },
                            { value: '30K+', label: 'Happy Attendees' },
                            { value: '3+', label: 'Years of Excellence' },
                        ].map((stat) => (
                            <div key={stat.label} className="text-center">
                                <p className="font-display text-2xl sm:text-3xl font-bold text-primary">{stat.value}</p>
                                <p className="text-xs text-muted-foreground mt-1 leading-tight">{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-background py-12 lg:py-16" aria-label="Event gallery">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
                        <div className="reveal-up">
                            <p className="section-label mb-2">Photo Gallery</p>
                            <h2 className="font-display text-section-title font-semibold text-foreground gold-underline">Events Photos</h2>
                        </div>
                        <Link to="/events" className="reveal-fade btn-outline-gold flex items-center gap-2 px-5 py-2.5 rounded-full text-xs self-start sm:self-auto">
                            <span>View All Events</span>
                            <Icon name="ArrowRightIcon" size={13} />
                        </Link>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-8">
                        {categories.map((cat) => (
                            <button
                                key={cat}
                                type="button"
                                onClick={() => setActiveCategory(cat)}
                                className={`px-4 py-2 rounded-full text-xs font-bold tracking-wider uppercase transition-all duration-200 ${
                                    activeCategory === cat ? 'btn-gold' : 'border border-border/60 text-muted-foreground hover:border-primary hover:text-primary'
                                }`}
                            >
                                {cat === 'All' ? `All (${events.length})` : cat}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="text-center py-20">
                            <p className="text-muted-foreground text-base">Loading gallery…</p>
                        </div>
                    ) : filteredEvents.length === 0 ? (
                        <div className="text-center py-20">
                            <p className="text-muted-foreground text-base">
                                {events.length === 0
                                    ? 'No gallery images yet.'
                                    : 'No images found in this category.'}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
                            {filteredEvents.map((event, idx) => (
                                <button
                                    key={event.id}
                                    type="button"
                                    onClick={() => openLightbox(idx)}
                                    className={`reveal-up stagger-${(idx % 6) + 1} group relative overflow-hidden rounded-2xl border border-border/50 bg-card shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
                                    aria-label={`View ${event.title}`}
                                >
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <img src={event.images[0].src} alt={event.images[0].alt} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                                    </div>
                                    <div className="absolute top-3 left-3">
                                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase bg-black/60 text-white/90 backdrop-blur-sm">{event.category}</span>
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    <div className="absolute inset-x-0 bottom-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                                        <p className="text-white text-sm font-medium text-left line-clamp-2">{event.title}</p>
                                        <p className="text-white/70 text-xs mt-0.5">{event.location} · {event.year}</p>
                                    </div>
                                    <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <Icon name="MagnifyingGlassPlusIcon" size={14} className="text-white" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="mt-12 text-center">
                        <p className="text-muted-foreground text-sm mb-4">
                            Showing {filteredEvents.length} image{filteredEvents.length !== 1 ? 's' : ''}
                            {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
                        </p>
                        <Link to="/events" className="btn-gold inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full text-sm font-bold tracking-widest uppercase">
                            <span>Book Your Next Event</span>
                            <Icon name="ArrowRightIcon" size={16} className="text-black" />
                        </Link>
                    </div>
                </div>
            </section>

            <section className="bg-secondary border-t border-border/40 py-16 lg:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                        <div className="reveal-up text-center lg:text-left">
                            <p className="section-label mb-3">Don&apos;t Miss Out</p>
                            <h2 className="font-display text-section-title font-semibold text-foreground gold-underline">Upcoming Events</h2>
                            <p className="text-muted-foreground leading-relaxed text-base mt-6 max-w-lg">
                                Be part of the next unforgettable experience. Awakasha Entertainment brings world-class Sri Lankan music, film, and theatre to cities across Australia.
                            </p>
                        </div>
                        <div className="reveal-fade flex flex-col sm:flex-row gap-4" style={{ transitionDelay: '200ms' }}>
                            <Link to="/events" className="btn-gold flex items-center justify-center gap-2.5 px-8 py-4 rounded-full text-sm font-bold tracking-widest uppercase">
                                <span>View All Events</span>
                                <Icon name="ArrowRightIcon" size={16} className="text-black" />
                            </Link>
                            <Link to="/contact" className="btn-outline-gold flex items-center justify-center gap-2.5 px-8 py-4 rounded-full text-sm">
                                <span>Contact Us</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <EventGalleryLightbox events={filteredEvents} state={lightbox} onClose={closeLightbox} onPrev={prevImage} onNext={nextImage} />
        </>
    );
}
